import { Drawer } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { getEngagementPlanTasksThunk, cancelEngagementPlanThunk } from '@redux/tasks/tasksThunks';
import { EngagementPlanTask } from '@redux/tasks/tasksTypes';
import { ReduxState } from '@redux/types';
import EngagementPlanDefaultImage from 'assets/images/EngagementPlanDefaultImage.jpg';
import EngagementPlanDefaultImage2 from 'assets/images/EngagementPlanDefaultImage2.png';
import { BackIconButton } from 'components/Button/BackButton/BackIconButton';
import { Button } from 'components/Button/Button';
import { CloseIconButton } from 'components/Button/CloseButton/CloseIconButton';
import { ConfirmationDialogV2 } from 'components/ConfirmationDialog/ConfirmationDialogV2';
import { WarningCircleIcon } from 'components/Icons/WarningCircleIcon';
import { WaveformIcon } from 'components/Icons/WaveformIcon';
import { PlanTaskDescriptionString } from 'components/PlanTaskDescriptionString/PlanTaskDescriptionString';
import { TaskTypeIcon } from 'components/TaskTypeIcon';
import { Text } from 'components/Typography';
import { formatDate } from 'utils/time';
import style from './EngagementPlanDetailsDrawer.scss';

const mapStateToProps = (state: ReduxState) => ({
  // We can access individual engagement plan details from the tasks state
});

const mapDispatchToProps = {
  getEngagementPlanTasks: getEngagementPlanTasksThunk,
  cancelEngagementPlan: cancelEngagementPlanThunk
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface EngagementPlanDetailsDrawerProps extends PropsFromRedux {
  open: boolean;
  onClose: () => void;
  engagementPlanId?: string;
  patientEngagementPlanId?: string;
  title?: string;
  description?: string;
  backgroundImage?: string;
  startDate?: string;
  status?: string;
  durationDays?: number;
  currentDay?: number;
  isGlobalPlan?: boolean;
  onRefreshData?: () => void;
}

const EngagementPlanDetailsDrawer: React.FC<EngagementPlanDetailsDrawerProps> = ({
  open,
  onClose,
  engagementPlanId,
  patientEngagementPlanId,
  title,
  description,
  backgroundImage,
  startDate,
  status,
  durationDays,
  currentDay,
  getEngagementPlanTasks,
  cancelEngagementPlan,
  onRefreshData,
  isGlobalPlan
}) => {
  const [planTasks, setPlanTasks] = useState<EngagementPlanTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState<EngagementPlanTask | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const imageLink = !isGlobalPlan ? backgroundImage || EngagementPlanDefaultImage2 : EngagementPlanDefaultImage;

  useEffect(() => {
    const loadEngagementPlanDetails = async () => {
      if (!engagementPlanId) return;

      setLoading(true);
      try {
        const tasks = await getEngagementPlanTasks(engagementPlanId);
        setPlanTasks(tasks);
        // For now, we'll work with the provided props for plan details
        // since the existing thunk only returns tasks
      } catch (error) {
        console.error('Failed to load engagement plan details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (open && engagementPlanId) {
      loadEngagementPlanDetails();
    } else {
      setPlanTasks([]);
      setSelectedTask(null);
    }
  }, [open, engagementPlanId]);

  const handleTaskClick = (task: EngagementPlanTask) => {
    setSelectedTask(task);
  };

  const handleBackClick = () => {
    setSelectedTask(null);
  };

  const handleClose = () => {
    setPlanTasks([]);
    setSelectedTask(null);
    onClose();
  };

  const handleCancelPlan = () => {
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!patientEngagementPlanId) return;

    setCancelling(true);
    try {
      await cancelEngagementPlan(patientEngagementPlanId);
      if (onRefreshData) {
        onRefreshData();
      }
      handleClose();
    } catch (error) {
      console.error('Failed to cancel engagement plan:', error);
    } finally {
      setCancelling(false);
      setCancelDialogOpen(false);
    }
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
  };

  const getStatusText = () => {
    if (status === 'COMPLETED') {
      return 'Completed';
    }
    if (status === 'ACTIVE' && currentDay && durationDays) {
      return `Day ${Math.min(currentDay, durationDays)} of ${durationDays}`;
    }
    return status || '';
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <div className={style.drawerContainer}>
        <div className={style.content}>
          {selectedTask ? (
            // Task detail view
            <>
              <div className={style.taskDetailHeader}>
                <BackIconButton onClick={handleBackClick} />
                <CloseIconButton onClick={handleClose} />
              </div>
              <div className={style.taskDetailContent}>
                <Text variant="2xl" weight={600}>
                  {selectedTask.taskName}
                </Text>
                <PlanTaskDescriptionString task={selectedTask} containerStyle={style.taskDetailMeta} />
                {selectedTask.deliverableMessage && (
                  <div className={style.taskSummaryBox}>
                    <div className={style.summaryHeader}>
                      <div className={style.waveformIconContainer}>
                        <WaveformIcon width={24} height={24} />
                      </div>
                      <Text variant="md" weight={500} className={style.summaryHeaderText}>
                        Message summary
                      </Text>
                    </div>
                    <Text variant="md" className={style.taskSummaryText}>
                      {selectedTask.deliverableMessage}
                    </Text>
                  </div>
                )}
              </div>
            </>
          ) : (
            // Plan overview
            <>
              <div className={style.closeButton}>
                <CloseIconButton onClick={handleClose} />
              </div>

              {/* Header section with image and title */}
              <div className={style.sectionHeader}>
                <div className={style.planHeaderImage} style={{ backgroundImage: `url(${imageLink})` }} />
                <Text className={style.planTitle}>{title}</Text>
                {description && <Text variant="md">{description}</Text>}
                <Text variant="sm" className={style.planDetails}>
                  {`${durationDays}-day${durationDays === 1 ? '' : 's'} program · ${planTasks.length} tasks`}
                </Text>
              </div>

              {/* Tasks list */}
              <div className={style.tasksList}>
                {loading ? (
                  <div className={style.loadingState}>
                    <Text variant="md">Loading tasks...</Text>
                  </div>
                ) : (
                  planTasks.map((task, index) => (
                    <div key={index} className={style.taskItem} onClick={() => handleTaskClick(task)}>
                      <div className={style.taskHeader}>
                        <TaskTypeIcon taskType={task.taskType} size={30} />
                        <div className={style.taskInfo}>
                          <Text variant="md" weight={500}>
                            {task.taskName}
                          </Text>
                          <PlanTaskDescriptionString task={task} containerStyle={style.taskSubtitle} />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Status and action section */}
              <div className={style.statusSection}>
                <div className={style.statusRow}>
                  <div className={style.statusText}>
                    <Text variant="md" className={style.statusLeft}>
                      Started {formatDate(startDate)}
                    </Text>
                    <span className={style.dotSeparator}>·</span>
                    <Text variant="md" className={style.statusRight}>
                      {getStatusText()}
                    </Text>
                  </div>

                  {status === 'ACTIVE' && (
                    <div className={style.actionButtons}>
                      <Button variant="outlined" color="error" onClick={handleCancelPlan} disabled={cancelling} className={style.cancelButton}>
                        {cancelling ? 'Cancelling...' : 'Cancel Plan'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmationDialogV2
        open={cancelDialogOpen}
        icon={WarningCircleIcon}
        title="Are you sure you want to cancel?"
        description="This cannot be undone."
        proceedEvent={handleCancelConfirm}
        proceedEventText="Cancel Plan"
        closeEvent={handleCancelDialogClose}
        closeEventText="Keep Plan"
      />
    </Drawer>
  );
};

export default connector(EngagementPlanDetailsDrawer);
